import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';

import {useEventFamilies} from 'src/hooks/useEventFamilies';
import {BLANK_FAMILY} from 'src/modules/user-settings/constants';
import {NotificationType} from 'src/modules/user-settings/typedefs';
import {EVENT_UPDATE_SUBSCRIPTION_INFO} from 'src/modules/user-settings/user-settings-hovers';
import {
    convertCheckedIdsToEventSubscriptions,
} from 'src/modules/user-settings/utils/convertCheckedIdsToEventSubscriptions';
import {createCheckedIdsFromEventSubscriptions} from 'src/modules/user-settings/utils/createCheckedIdsFromEventSubscriptions';
import {createTree} from 'src/modules/user-settings/utils/createTree';
import {getAllSources} from 'src/modules/user-settings/utils/getAllSources';
import {saveUserSettings} from 'src/openapi/apis/UserControllerApi';
import {UserSettings} from 'src/openapi/models/UserSettings';
import {useAppDispatch} from 'src/store';

import {ChangeTreeType, CheckboxTree} from '@pb/kit-component-checkbox-tree';
import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group, Item} from '@pb/kit-component-group';
import {InfoIconButton} from '@pb/kit-component-icon-button';
import StateButton from '@pb/kit-component-state-button';

type Props = {
    userSettings: UserSettings | null,
    onChangeStateButton: (event: ChangeEvent<HTMLInputElement>) => void,
};

export const EventUpdateSubscription = (props: Props) => {
    const {
        userSettings,
        onChangeStateButton,
    } = props;

    const dispatch = useAppDispatch();
    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const sourcesList = useMemo(() => getAllSources().toSorted(), []);
    const eventFamilies = useEventFamilies();
    const familiesList = useMemo(() => [BLANK_FAMILY, ...eventFamilies], [eventFamilies]);

    const treeData = useMemo(() => {
        const disabled = Boolean(!userSettings?.emailEventUpdateSubscription);
        return createTree(sourcesList, familiesList, disabled);
    }, [sourcesList, familiesList, userSettings]);

    useEffect(() => {
        const checkedIdsFromEventSubscriptions =
            createCheckedIdsFromEventSubscriptions(userSettings?.eventUpdateSubscriptions || [], eventFamilies);

        setCheckedIds(checkedIdsFromEventSubscriptions);
    }, [userSettings, eventFamilies]);

    const onChange = useCallback((changeTreeType: ChangeTreeType) => {
        const checkedId = changeTreeType.checkedId;
        const checkedIdsList = changeTreeType.checkedIdsList;

        if (userSettings && checkedId) {
            const nextSubscriptions =
                convertCheckedIdsToEventSubscriptions(checkedIdsList, eventFamilies);

            const nextSettings = {
                ...userSettings,
                eventUpdateSubscriptions: nextSubscriptions,
            };

            dispatch(saveUserSettings({userSettings: nextSettings}));
        }
    }, [dispatch, userSettings, eventFamilies]);

    return (
        <Group direction="v" className="pl-xxxl">
            <Group direction="v">
                <Group gap="xs" alignItems="center">
                    <Item>Events Daily Updates</Item>
                    <Dropdown
                        hoverEnabled={true}
                        clickEnabled={false}
                        maxWidth={500}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                        )}>
                        {EVENT_UPDATE_SUBSCRIPTION_INFO}
                    </Dropdown>
                </Group>
                <StateButton
                    captions={{off: 'OFF', on: 'ON'}}
                    name={NotificationType.EVENT_UPDATE_SUBSCRIPTION}
                    checked={userSettings?.emailEventUpdateSubscription}
                    onChange={onChangeStateButton}
                />
            </Group>
            <CheckboxTree
                data={treeData}
                checkedIdsList={checkedIds}
                onChange={onChange}
                usePropsOnly={true}
            />
        </Group>
    );
};
